/**
 * The picture beside the sign-in and join-the-network forms: a small glowing network of Yellow Dots
 * with "You" at the centre and the things the network finds around you - tutors, coaches, trainers,
 * schools, colleges, jobs - each on its own node.
 *
 * <p>Deliberately abstract. It is a picture of an idea (what is near you), not of a place, so there
 * is no map outline to be right or wrong about.
 *
 * <p>Drawn in code rather than shipped as an image so it stays sharp at any size, weighs nothing and
 * follows the palette. Everything that moves sits inside a {@code prefers-reduced-motion:
 * no-preference} query, so a visitor who asked their system for less motion sees a still picture.
 */

/** The picture's own coordinate space. */
const W = 280
const H = 310
const CENTER: [number, number] = [140, 152]

/** What the network finds around you. `side` is which way its label sits from the node. */
const NODES: { label: string; x: number; y: number; side: 'l' | 'r' }[] = [
  { label: 'Tutors', x: 214, y: 84, side: 'r' },
  { label: 'Schools', x: 236, y: 184, side: 'r' },
  { label: 'Coaches', x: 190, y: 262, side: 'r' },
  { label: 'Trainers', x: 92, y: 250, side: 'l' },
  { label: 'Jobs', x: 48, y: 156, side: 'l' },
  { label: 'Colleges', x: 84, y: 64, side: 'l' },
]

/** Unlabelled dots of the network - other people, other places - hanging off the named ones. */
const EXTRAS: { x: number; y: number; to: number }[] = [
  { x: 160, y: 30, to: 0 },
  { x: 262, y: 118, to: 0 },
  { x: 268, y: 236, to: 1 },
  { x: 140, y: 296, to: 2 },
  { x: 40, y: 232, to: 3 },
  { x: 14, y: 100, to: 4 },
  { x: 36, y: 34, to: 5 },
]

/** Rings around "You" - how far around, drawn as the faint ripples of a signal. */
const RINGS = [46, 88, 132]

/** A cheap deterministic scatter, so the field is spread out but never differs between renders. */
const hash = (n: number) => {
  const s = Math.sin(n * 127.1) * 43758.5453
  return s - Math.floor(s)
}

/** The faint field of small dots behind everything - sparse on purpose. */
const FIELD = Array.from({ length: 64 }, (_, i) => ({
  x: hash(i + 1) * W,
  y: hash(i + 101) * H,
  r: 0.7 + hash(i + 201) * 1.3,
  o: 0.12 + hash(i + 301) * 0.28,
  twinkle: hash(i + 401) > 0.7 ? hash(i + 501) * 3.4 : null,
}))

/** A curve between two points, bowed to one side so links read as arcs rather than spokes. */
function arc(a: [number, number], b: [number, number], bow: number): string {
  const mx = (a[0] + b[0]) / 2
  const my = (a[1] + b[1]) / 2
  const dx = b[0] - a[0]
  const dy = b[1] - a[1]
  // The perpendicular, scaled by how long the link is.
  return `M${a[0]},${a[1]} Q${mx - dy * bow},${my + dx * bow} ${b[0]},${b[1]}`
}

const pct = (v: number, of: number) => `${(v / of) * 100}%`

/** What the picture says. Defaults are the member sign-in's; the ops console passes its own. */
export interface ShowcaseCopy {
  /** Six labels, one per node, clockwise from top right. */
  labels?: [string, string, string, string, string, string]
  /** The badge under the centre dot. */
  centre?: string
  /** The headline's two lines - the second in yellow. */
  title?: [string, string]
  subtitle?: string
}

export function AuthShowcase({
  labels,
  centre = 'You',
  title = ['Discover What’s Next,', 'Around You.'],
  subtitle = 'Find trusted tutors, coaches, trainers, schools, colleges and jobs near you.',
}: ShowcaseCopy = {}) {
  const nodes = labels ? NODES.map((n, i) => ({ ...n, label: labels[i] })) : NODES
  return (
    <div className="relative flex h-full min-h-[30rem] flex-col overflow-hidden rounded-3xl bg-gradient-to-br from-deep-800 via-deep-900 to-[#050d1a] p-8 text-white xl:p-12">
      <style>{`
        @media (prefers-reduced-motion: no-preference) {
          .yd-twinkle { animation: yd-twinkle 3.4s ease-in-out infinite; }
          .yd-flow { stroke-dasharray: 3 7; animation: yd-flow 2.6s linear infinite; }
          .yd-pulse { transform-box: fill-box; transform-origin: center; animation: yd-pulse 2.8s ease-out infinite; }
          .yd-ring { transform-box: fill-box; transform-origin: center; animation: yd-ring 6s ease-out infinite; }
        }
        @keyframes yd-twinkle { 0%,100% { opacity: .2 } 50% { opacity: .9 } }
        @keyframes yd-flow { to { stroke-dashoffset: -20 } }
        @keyframes yd-pulse { 0% { transform: scale(.7); opacity: .8 } 100% { transform: scale(3.2); opacity: 0 } }
        @keyframes yd-ring { 0% { transform: scale(.35); opacity: .5 } 100% { transform: scale(1); opacity: 0 } }
      `}</style>

      {/* The warm light the network sits in. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(520px 480px at 50% 34%, rgba(255,190,26,0.22) 0%, rgba(255,190,26,0.07) 46%, rgba(5,13,26,0) 76%)',
        }}
      />

      {/* The network takes whatever height is left above the words, in a box that keeps its own
          proportions so the labels stay on their nodes. */}
      <div className="pointer-events-none relative min-h-0 flex-1" aria-hidden>
      <div
        className="absolute left-1/2 top-0 h-full -translate-x-1/2"
        style={{ aspectRatio: `${W} / ${H}` }}
      >
        <svg className="absolute inset-0 h-full w-full overflow-visible" viewBox={`0 0 ${W} ${H}`}>
          <defs>
            <radialGradient id="yd-halo">
              <stop offset="0" stopColor="#FFBE1A" stopOpacity="0.55" />
              <stop offset="1" stopColor="#FFBE1A" stopOpacity="0" />
            </radialGradient>
            <linearGradient id="yd-link" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0" stopColor="#FFBE1A" stopOpacity="0.95" />
              <stop offset="1" stopColor="#FDE68A" stopOpacity="0.4" />
            </linearGradient>
          </defs>

          {FIELD.map((d, i) => (
            <circle
              key={i}
              cx={d.x}
              cy={d.y}
              r={d.r}
              fill="#FDE68A"
              opacity={d.o}
              className={d.twinkle === null ? undefined : 'yd-twinkle'}
              style={d.twinkle === null ? undefined : { animationDelay: `${d.twinkle}s` }}
            />
          ))}

          {RINGS.map((r, i) => (
            <circle
              key={r}
              cx={CENTER[0]}
              cy={CENTER[1]}
              r={r}
              fill="none"
              stroke="#FDE68A"
              strokeOpacity={0.16 - i * 0.03}
              strokeWidth="0.8"
              strokeDasharray="1.5 4"
            />
          ))}
          <circle cx={CENTER[0]} cy={CENTER[1]} r={132} fill="none" stroke="#FFBE1A" strokeWidth="1" className="yd-ring" />

          {/* The quiet links first - between neighbours, and out to the unnamed dots. */}
          {nodes.map((n, i) => {
            const next = nodes[(i + 1) % nodes.length]
            return (
              <path
                key={`n-${n.label}`}
                d={arc([n.x, n.y], [next.x, next.y], 0.12)}
                fill="none"
                stroke="#FDE68A"
                strokeOpacity="0.22"
                strokeWidth="0.9"
              />
            )
          })}
          {EXTRAS.map((e) => (
            <path
              key={`e-${e.x}-${e.y}`}
              d={`M${nodes[e.to].x},${nodes[e.to].y} L${e.x},${e.y}`}
              stroke="#FDE68A"
              strokeOpacity="0.2"
              strokeWidth="0.8"
            />
          ))}
          {EXTRAS.map((e) => (
            <g key={`d-${e.x}-${e.y}`}>
              <circle cx={e.x} cy={e.y} r={7} fill="url(#yd-halo)" />
              <circle cx={e.x} cy={e.y} r={2.4} fill="#FFBE1A" stroke="#0A192F" strokeWidth="0.9" />
            </g>
          ))}

          {/* Then the ones that matter: "You" to each thing found around you. */}
          {nodes.map((n, i) => (
            <path
              key={`c-${n.label}`}
              d={arc(CENTER, [n.x, n.y], i % 2 === 0 ? 0.14 : -0.14)}
              fill="none"
              stroke="url(#yd-link)"
              strokeWidth="1.5"
              strokeLinecap="round"
              className="yd-flow"
            />
          ))}

          {nodes.map((n) => (
            <g key={n.label}>
              <circle cx={n.x} cy={n.y} r={20} fill="url(#yd-halo)" />
              <circle cx={n.x} cy={n.y} r={4} fill="none" stroke="#FFBE1A" strokeWidth="1" className="yd-pulse" />
              <circle cx={n.x} cy={n.y} r={4.2} fill="#FFBE1A" stroke="#0A192F" strokeWidth="1.6" />
            </g>
          ))}

          {/* You. */}
          <circle cx={CENTER[0]} cy={CENTER[1]} r={44} fill="url(#yd-halo)" />
          <circle cx={CENTER[0]} cy={CENTER[1]} r={8} fill="none" stroke="#FFBE1A" strokeWidth="1.2" className="yd-pulse" />
          <circle cx={CENTER[0]} cy={CENTER[1]} r={8.5} fill="#FFBE1A" stroke="#0A192F" strokeWidth="3" />
        </svg>

        {nodes.map((n) => (
          <span
            key={n.label}
            className={`absolute -translate-y-1/2 whitespace-nowrap rounded-full border border-white/15 bg-white/10 px-2.5 py-1 text-[11px] font-semibold text-white/90 backdrop-blur-sm ${
              n.side === 'r' ? 'ml-4' : '-translate-x-full -ml-4'
            }`}
            style={{ left: pct(n.x, W), top: pct(n.y, H) }}
          >
            <span className="mr-1.5 inline-block h-1.5 w-1.5 rounded-full bg-dot-400 align-middle" />
            {n.label}
          </span>
        ))}
        <span
          className="absolute -translate-x-1/2 whitespace-nowrap rounded-full bg-dot-400 px-2.5 py-1 text-[11px] font-extrabold uppercase tracking-wide text-deep-900"
          style={{ left: pct(CENTER[0], W), top: pct(CENTER[1] + 20, H) }}
        >
          {centre}
        </span>
      </div>
      </div>

      <div className="relative mt-4 shrink-0">
        <h2 className="max-w-xl text-4xl font-extrabold leading-[1.1] tracking-tight xl:text-5xl [@media(max-height:760px)]:text-3xl">
          {title[0]}
          <br />
          <span className="text-dot-400">{title[1]}</span>
        </h2>
        <p className="mt-3 max-w-md text-base leading-relaxed text-white/75 [@media(max-height:760px)]:text-sm">
          {subtitle}
        </p>
      </div>
    </div>
  )
}
